import React, { useEffect, useState } from 'react';
import API from '../../services/api';
import styles from '../../styles/AdminDashboard.module.css';

export default function ReservationsPanel() {

    // RESERVATIONS
    const [reservations, setReservations] = useState([]);

    // LOADING
    const [loading, setLoading] = useState(false);

    // ERROR
    const [error, setError] = useState('');

    // SUCCESS MESSAGE
    const [successMessage, setSuccessMessage] = useState('');

    // FETCH RESERVATIONS
    useEffect(() => {
        // INITIAL FETCH
        fetchReservations();

        // AUTO REFRESH EVERY 10 SECONDS
        const interval = setInterval(() => {
            fetchReservations();
        }, 10000);

        // CLEANUP
        return () => clearInterval(interval);
    }, []);

    const fetchReservations = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await API.get('/reservations');
            setReservations(response.data);

        } catch (err) {
            console.error(err);

            // 🚨 FIX: If the token is invalid or expired, clear it and boot them to login
            if (err.response?.status === 401 || err.response?.status === 403) {
                logout();
                return;
            }

            setError(
                err.response?.data?.message ||
                'Failed to fetch reservations'
            );

        } finally {
            setLoading(false);
        }
    };

    // DELETE RESERVATION
    const handleDelete = async (id) => {
        const confirmed = window.confirm(
            'Are you sure you want to delete this reservation?'
        );

        if (!confirmed) return;

        try {
            setError('');
            setSuccessMessage('');

            const response = await API.delete(`/reservations/${id}`);
            setSuccessMessage(response.data.message);
            fetchReservations();

        } catch (err) {
            console.error(err);

            if (err.response?.status === 401 || err.response?.status === 403) {
                logout();
                return;
            }

            setError(
                err.response?.data?.message ||
                'Failed to delete reservation'
            );
        }
    };

    // UPDATE STATUS
    const handleStatusChange = async (id, status) => {
        try {
            setError('');
            setSuccessMessage('');

            const response = await API.patch(`/reservations/${id}/status`, { status });
            setSuccessMessage(response.data.message);
            fetchReservations();

        } catch (err) {
            console.error(err);

            if (err.response?.status === 401 || err.response?.status === 403) {
                logout();
                return;
            }

            setError(
                err.response?.data?.message ||
                'Failed to update reservation status'
            );
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        window.location.href = '/admin/login';
    };

    // LOADING UI
    if (loading && reservations.length === 0) {
        return (
            <div className={styles.loading}>
                <h2>Loading reservations...</h2>
            </div>
        );
    }

    return (
        <div className={styles.dashboard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1 className={styles.title}>Admin Dashboard</h1>
                <button onClick={logout} style={{ padding: '8px 16px', cursor: 'pointer' }}>
                    Logout
                </button>
            </div>

            {/* ERROR MESSAGE */}
            {error && (
                <p className={`${styles.message} ${styles.error}`}>
                    {error}
                </p>
            )}

            {/* SUCCESS MESSAGE */}
            {successMessage && (
                <p className={`${styles.message} ${styles.success}`}>
                    {successMessage}
                </p>
            )}

            {/* EMPTY STATE */}
            {
                reservations.length === 0 ? (

                    <p className={styles.empty}>
                        No reservations found.
                    </p>

                ) : (

                    <>
                        {/* DESKTOP TABLE */}
                        <div className={styles.tableWrapper}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Phone & Email</th>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>People</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>

                                    {
                                        reservations.map((reservation) => (

                                            <tr key={reservation.id}>
                                                <td>{reservation.customer_name}</td>

                                                <td>
                                                    {reservation.phone}
                                                    <br />
                                                    {reservation.email}
                                                </td>

                                                <td>
                                                    {reservation.reservation_date.substr(8,2)}
                                                    -
                                                    {reservation.reservation_date.substr(5,2)}
                                                    -
                                                    {reservation.reservation_date.substr(0,4)}
                                                </td>

                                                <td>
                                                    {reservation.reservation_time}
                                                </td>

                                                <td>
                                                    {reservation.people_count}
                                                </td>

                                                <td>
                                                    <select
                                                        className={styles.statusSelect}
                                                        value={reservation.status}
                                                        onChange={(e) =>
                                                            handleStatusChange(
                                                                reservation.id,
                                                                e.target.value
                                                            )
                                                        }
                                                    >
                                                        <option value="pending">
                                                            Pending
                                                        </option>

                                                        <option value="confirmed">
                                                            Confirmed
                                                        </option>

                                                        <option value="cancelled">
                                                            Cancelled
                                                        </option>

                                                        <option value="completed">
                                                            Completed
                                                        </option>
                                                    </select>
                                                </td>

                                                <td>
                                                    <button
                                                        className={styles.deleteButton}
                                                        onClick={() =>
                                                            handleDelete(
                                                                reservation.id
                                                            )
                                                        }
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>

                                        ))
                                    }

                                </tbody>
                            </table>
                        </div>

                        {/* MOBILE CARDS */}
                        <div className={styles.mobileCards}>

                            {
                                reservations.map((reservation) => (

                                    <div
                                        className={styles.card}
                                        key={reservation.id}
                                    >

                                        <h3>
                                            {reservation.customer_name}
                                        </h3>

                                        <p>
                                            <strong>Email:</strong>
                                            <br />
                                            {reservation.email}
                                        </p>

                                        <p>
                                            <strong>Phone:</strong>
                                            <br />
                                            {reservation.phone}
                                        </p>

                                        <p>
                                            <strong>Date:</strong>
                                            <br />
                                            {reservation.reservation_date.substr(8,2)}
                                            -
                                            {reservation.reservation_date.substr(5,2)}
                                            -
                                            {reservation.reservation_date.substr(0,4)}
                                        </p>

                                        <p>
                                            <strong>Time:</strong>
                                            {' '}
                                            {reservation.reservation_time}
                                        </p>

                                        <p>
                                            <strong>People:</strong>
                                            {' '}
                                            {reservation.people_count}
                                        </p>

                                        <select
                                            className={styles.statusSelect}
                                            value={reservation.status}
                                            onChange={(e) =>
                                                handleStatusChange(
                                                    reservation.id,
                                                    e.target.value
                                                )
                                            }
                                        >
                                            <option value="pending">
                                                Pending
                                            </option>

                                            <option value="confirmed">
                                                Confirmed
                                            </option>

                                            <option value="cancelled">
                                                Cancelled
                                            </option>

                                            <option value="completed">
                                                Completed
                                            </option>
                                        </select>

                                        <button
                                            className={styles.deleteButton}
                                            onClick={() =>
                                                handleDelete(
                                                    reservation.id
                                                )
                                            }
                                        >
                                            Delete Reservation
                                        </button>

                                    </div>

                                ))
                            }

                        </div>
                    </>
                )
            }
        </div>
    );
}